import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetBusinessUnitQuery } from "@generated";
import { BusinessUnitForm } from "../BusinessUnitForm/BusinessUnitForm";
import { useBusinessUnitMutations } from "../api";
import { emptyBusinessUnitForm } from "../types";
import type { BusinessUnitFormValues } from "../types";

const FORM_ID = "business-unit-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useBusinessUnitMutations();
  // refetchOnMountOrArgChange so re-entering Edit (or arriving from a list
  // changed elsewhere) re-requests the record rather than trusting the cache.
  const { data, isLoading, refetch } = useV2GetBusinessUnitQuery(
    { id: Number(id) },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<BusinessUnitFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<BusinessUnitFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyBusinessUnitForm;
    return {
      name: attrs.name ?? "",
      externalName: attrs["external-name"] ?? "",
      streetAddress: attrs["street-address"] ?? "",
      street2: attrs.street2 ?? "",
      city: attrs.city ?? "",
      state: attrs.state ?? "",
      postalCode: attrs["postal-code"] ?? "",
      country: attrs.country ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: BusinessUnitFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update business unit", err);
      setToast({ variant: "danger", text: "Could not save business unit." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Business Unit"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <BusinessUnitForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
